// Job-API-Helper: SSE-Stream fuer Job-Updates + REST-Fallback (/jobs).
// Nutzt fetch + ReadableStream, weil EventSource keinen Authorization-Header setzen kann.

import getToken from '../auth/getToken';
import { loadTestHeaders } from '../auth/loadTestAuth';

const API = () => process.env.REACT_APP_BACKEND_URL;
const SCOPE = () => [`${process.env.REACT_APP_CLIENT_ID}/.default`];

async function authHeaders(instance) {
  const token = await getToken(instance, SCOPE());
  if (!token) throw new Error('Keine gueltige Authentifizierung');
  return {
    Authorization: `Bearer ${token}`,
    ...loadTestHeaders(),
  };
}

export async function fetchJobs(instance) {
  const headers = await authHeaders(instance);
  const res = await fetch(`${API()}/jobs`, { headers });
  if (!res.ok) {
    const msg = await res.text().catch(() => '');
    throw new Error(msg || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return Array.isArray(data?.jobs) ? data.jobs : [];
}

export async function cancelJob(instance, jobId) {
  const headers = {
    ...(await authHeaders(instance)),
    'Content-Type': 'application/json',
  };
  const res = await fetch(`${API()}/cancel_job/${jobId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({}),
  });
  if (!res.ok && res.status !== 404) {
    const msg = await res.text().catch(() => '');
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return res.status !== 404;
}

/**
 * Oeffnet einen SSE-Stream zu /jobs/stream.
 *
 * @param {object} opts
 * @param {import('@azure/msal-browser').PublicClientApplication} opts.instance
 * @param {(event: {type: string, jobs?: any[], job?: any}) => void} opts.onEvent
 * @param {(error: Error) => void} [opts.onError]
 * @param {AbortSignal} [opts.signal]
 */
export async function openJobsStream({ instance, onEvent, onError, signal }) {
  const headers = await authHeaders(instance);
  const res = await fetch(`${API()}/jobs/stream`, {
    method: 'GET',
    headers: { ...headers, Accept: 'text/event-stream' },
    signal,
  });
  if (!res.ok || !res.body) {
    const errText = await res.text().catch(() => '');
    throw new Error(errText || `HTTP ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split('\n\n');
      buffer = frames.pop() || '';
      for (const frame of frames) {
        if (!frame || frame.startsWith(':')) continue;
        const dataLine = frame
          .split('\n')
          .find((l) => l.startsWith('data:'));
        if (!dataLine) continue;
        const payload = dataLine.replace(/^data:\s*/, '');
        if (!payload) continue;
        try {
          const obj = JSON.parse(payload);
          onEvent(obj);
        } catch {
          // ignore non-JSON frames
        }
      }
    }
  } catch (err) {
    if (onError && err?.name !== 'AbortError') onError(err);
    throw err;
  }
}
